import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import MeetupLocationModal from '../components/MeetupLocationModal';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const STEPS_GUEST = ['Checkout Method', 'Cart Review', 'Guest Info', 'Shipping', 'Payment', 'Confirmation'];
const STEPS_AUTH = ['Cart Review', 'Shipping', 'Payment', 'Confirmation'];

// ── Stripe Card Form ──────────────────────────────────────────────
function StripeCardForm({ onToken, processing }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [name, setName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
      billing_details: { name },
    });
    if (error) { alert(error.message); return; }
    onToken(paymentMethod.id);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Cardholder Name</label>
        <input className="form-input" placeholder="John Smith" value={name} onChange={e => setName(e.target.value)} required />
      </div>
      <div className="form-group">
        <label className="form-label">Card Details</label>
        <div style={{ border: '1.5px solid var(--border)', borderRadius: 9, padding: '13px 14px', background: 'white' }}>
          <CardElement options={{ style: { base: { fontSize: '14px', fontFamily: '"DM Sans", sans-serif', color: '#1A1A1A', '::placeholder': { color: '#9A928A' } } } }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {['💳 Visa', '💳 Mastercard', '💳 Amex', '🍎 Apple Pay', 'G Google Pay'].map(m => (
          <div key={m} style={{ padding: '4px 9px', background: '#F4F3FF', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#635BFF' }}>{m}</div>
        ))}
      </div>
      <button type="submit" className="btn btn-full" disabled={!stripe || processing}
        style={{ background: processing ? '#888' : '#635BFF', color: 'white', border: 'none', borderRadius: 11, padding: '14px', fontSize: 15, fontWeight: 700 }}>
        {processing ? <><span className="spinner" /> Processing...</> : `🔒 Pay with Card`}
      </button>
    </form>
  );
}

export default function CheckoutPage() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { cartItem, checkoutData, setCheckout, clearCart } = useCart();

  // Determine if guest or authenticated
  const [checkoutMode, setCheckoutMode] = useState(user ? 'auth' : null); // null = choose, 'guest' or 'auth'
  const isGuest        = checkoutMode === 'guest';
  const steps          = checkoutMode === null ? [] : isGuest ? STEPS_GUEST : STEPS_AUTH;

  const [step,        setStep]        = useState(0);
  const [payMethod,   setPayMethod]   = useState('stripe');
  const [processing,  setProcessing]  = useState(false);
  const [orderId,     setOrderId]     = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [bookingId,   setBookingId]   = useState(null);
  const [savedListing, setSavedListing] = useState(null); // Save listing for confirmation page
  const [error,       setError]       = useState('');

  // Guest info
  const [guest, setGuest] = useState({ name: '', email: '', confirmEmail: '' });

  // Shipping / address
  const [address, setAddress] = useState({
    name:    user ? `${user.firstName} ${user.lastName}` : '',
    street:  '',
    city:    user?.locationCity || '',
    state:   user?.locationState || '',
    zip:     '',
    country: 'US',
  });

  // Shipping rates
  const [rates,        setRates]        = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);

  // Meetup locations for rentals & local pickup
  const [meetupPlaces,    setMeetupPlaces]    = useState([]);
  const [loadingPlaces,   setLoadingPlaces]   = useState(false);
  const [selectedMeetup,  setSelectedMeetup]  = useState(null);
  const [customMeetup,    setCustomMeetup]    = useState(null);
  const [deliveryMethod,  setDeliveryMethod]  = useState(null); // 'pickup' or 'shipping'
  const [pickupDate,      setPickupDate]      = useState(null); // Local pickup date
  const [pickupTime,      setPickupTime]      = useState(null); // Pickup time slot (e.g., "10:00-11:00")
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [preferredLocations, setPreferredLocations] = useState([]);
  const [loadingPreferred, setLoadingPreferred] = useState(false);

  // Coinbase charge
  const [cbCharge, setCbCharge] = useState(null);

  // Bundle discount
  const [sellerBundle, setSellerBundle] = useState(null);

  // Coupon
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // When user logs in mid-checkout, auto-switch to auth mode
  useEffect(() => {
    if (user && checkoutMode === null) {
      setCheckoutMode('auth');
    }
  }, [user]);

  // When checkout mode changes, set appropriate step
  useEffect(() => {
    if (checkoutMode === 'guest') {
      setStep(1); // Cart review step for guests
    } else if (checkoutMode === 'auth') {
      setStep(0); // Cart review step for auth users
    }
  }, [checkoutMode]);

  // Pre-populate rental dates from cart extras and save listing
  useEffect(() => {
    if (cartItem?.startDate && !checkoutData.startDate) {
      setCheckout({ startDate: cartItem.startDate, endDate: cartItem.endDate, rentDays: cartItem.rentDays || 1 });
    }
    if (cartItem?.listing) {
      setSavedListing(cartItem.listing);
      const sellerId = cartItem.listing.seller_id || cartItem.listing.user_id;
      if (sellerId) {
        API.get(`/users/${sellerId}/bundle-discount`)
          .then(r => setSellerBundle(r.data.bundleDiscount || null))
          .catch(() => {});
      }
    }
  }, [cartItem]);

  // Set pickup date to rental start date for rentals (must be before early return)
  useEffect(() => {
    if (cartItem?.listing?.listing_type === 'rent' && !pickupDate && checkoutData.startDate) {
      setPickupDate(checkoutData.startDate);
    }
  }, [cartItem, pickupDate, checkoutData.startDate]);

  // Empty cart state
  if (!cartItem && step === 0) return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '64px' }}>🛒</div>
      <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>Your cart is empty</h2>
      <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>Browse listings to find something you like.</p>
      <button onClick={() => navigate('/browse')} style={{ marginTop: '8px', padding: '12px 28px', background: '#111', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' }}>
        Browse Listings
      </button>
    </div>
  );

  const listing     = cartItem?.listing || savedListing;
  const isRent      = listing.listing_type === 'rent';
  const isAuction   = listing.listing_type === 'auction';
  const rentDays    = checkoutData.rentDays || 1;
  const itemPrice   = isRent
    ? parseFloat(listing.price) * rentDays
    : cartItem?.offerPrice
    ? parseFloat(cartItem.offerPrice)
    : cartItem?.buyItNow && listing.buy_it_now_price
    ? parseFloat(listing.buy_it_now_price)
    : parseFloat(listing.current_bid || listing.price || 0);
  const shipping    = selectedRate?.rate || 0;
  const platformFee = itemPrice * 0.10;
  const deposit     = isRent ? parseFloat(listing.deposit_amount || 0) : 0;

  let couponDiscount = 0;
  if (coupon) {
    if (coupon.discountType === 'percentage') {
      couponDiscount = itemPrice * (coupon.discountAmount / 100);
    } else {
      couponDiscount = coupon.discountAmount;
    }
    couponDiscount = Math.min(couponDiscount, itemPrice);
  }

  const total       = Math.max(0, itemPrice - couponDiscount + shipping + platformFee + deposit);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  // Step index helpers (guests have decision step at 0, then cart at 1, etc)
  const STEP_DECISION = 0; // guests only
  const STEP_CART     = isGuest ? 1 : 0;
  const STEP_GUEST    = isGuest ? 2 : null;
  const STEP_SHIPPING = isGuest ? 3 : 1;
  const STEP_PAYMENT  = isGuest ? 4 : 2;
  const STEP_CONFIRM  = isGuest ? 5 : 3;

  const fetchRates = async () => {
    if (!listing.shipping_available) return;
    setLoadingRates(true);
    try {
      const { data } = await API.post('/shipping/rates', {
        fromZip: listing.location_zip, fromCity: listing.location_city,
        fromState: listing.location_state, fromName: `${listing.first_name} ${listing.last_name}`,
        toZip: address.zip, toCity: address.city, toState: address.state,
        toName: address.name, toStreet: address.street,
        weightLbs: listing.weight_lbs || 1, lengthIn: listing.length_in || 12,
        widthIn: listing.width_in || 8, heightIn: listing.height_in || 6,
      });
      setRates(data.rates);
      if (data.rates[0]) setSelectedRate(data.rates[0]);
    } catch { setRates([]); }
    finally { setLoadingRates(false); }
  };

  const fetchPreferredLocations = async () => {
    console.log('Fetching preferred locations for listing:', listing?.id);
    if (!listing?.id) {
      console.warn('No listing ID available');
      return;
    }
    setLoadingPreferred(true);
    try {
      const { data } = await API.get(`/locations/listings/${listing.id}/preferred`);
      console.log('Preferred locations response:', data);
      setPreferredLocations(data.locations || []);
    } catch (err) {
      console.error('Failed to fetch preferred locations:', err.message, err.response?.data);
      setPreferredLocations([]);
    } finally {
      setLoadingPreferred(false);
    }
  };

  const fetchNearbyPlaces = async () => {
    setLoadingPlaces(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation not supported by your browser. Please enter location manually.');
      setLoadingPlaces(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const { data } = await API.get('/locations/nearby', {
            params: { lat: latitude, lng: longitude, radius: 5000 },
          });

          if (data.places && data.places.length > 0) {
            setMeetupPlaces(data.places);
            setSelectedMeetup(data.places[0]);
            setCustomMeetup(null);
          } else {
            setError('No nearby locations found. Please enter a location manually.');
            setMeetupPlaces([]);
          }
        } catch (err) {
          console.error('Failed to fetch nearby places:', err);
          setError('Failed to fetch locations. Please try again or enter manually.');
          setMeetupPlaces([]);
        } finally {
          setLoadingPlaces(false);
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        let errorMsg = 'Unable to access your location. ';
        if (err.code === 1) errorMsg += 'Please enable location access in your browser settings.';
        else if (err.code === 2) errorMsg += 'Location service unavailable.';
        else if (err.code === 3) errorMsg += 'Location request timed out.';
        else errorMsg += 'Please enter a location manually.';

        setError(errorMsg);
        setLoadingPlaces(false);
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  const handleLocationSelect = (location) => {
    setSelectedMeetup({
      name: location.name,
      address: location.address,
      lat: location.lat,
      lng: location.lng,
      label: location.name
    });
    setShowLocationModal(false);
    setCustomMeetup(null);
    setMeetupPlaces([]);
  };

  // ── Validate and apply coupon ────────────────────────────────────
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('');
      setCoupon(null);
      return;
    }

    setCouponLoading(true);
    setCouponError('');
    try {
      const { data } = await API.get(`/coupons/${couponCode.toUpperCase()}/validate`);
      setCoupon(data.coupon);
      setCouponError('');
    } catch (err) {
      setCoupon(null);
      setCouponError(err.response?.data?.error || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  // ── Build order/booking payload ───────────────────────────────────
  const guestPayload = isGuest ? { guestEmail: guest.email, guestName: guest.name } : {};

  const createOrderOrBooking = async () => {
    const meetup = selectedMeetup || customMeetup;
    let orderId, referenceId, referenceType;

    if (isRent) {
      const { data } = await API.post('/bookings', {
        listingId: listing.id, startDate: checkoutData.startDate,
        endDate: checkoutData.endDate, notes: checkoutData.notes,
        meetupLocationName: meetup?.name || meetup?.label,
        meetupLocationAddress: meetup?.address || meetup?.address,
        meetupLocationLat: meetup?.lat,
        meetupLocationLng: meetup?.lng,
        pickupDate: pickupDate,
        pickupTimeSlot: pickupTime,
        ...guestPayload,
      });
      setBookingId(data.booking.id);
      referenceId = data.booking.id;
      referenceType = 'booking';
    } else if (cartItem.existingOrderId) {
      // Auction win — order already exists, just pay for it
      orderId = cartItem.existingOrderId;
      setOrderId(orderId);
      setOrderNumber(cartItem.existingOrderNumber || '');
      referenceId = orderId;
      referenceType = 'order';
    } else {
      const { data } = await API.post('/orders', {
        listingId: listing.id, shippingAddress: address,
        shippingCarrier: selectedRate?.carrier, shippingCost: selectedRate?.rate || 0,
        buyItNow: cartItem.buyItNow,
        rentalToConvert: cartItem.rentalToConvert,
        bookingId: cartItem.bookingId,
        offerId: cartItem.offerId || undefined,
        ...guestPayload,
      });
      orderId = data.order.id;
      setOrderId(orderId);
      setOrderNumber(data.order.order_number);
      referenceId = orderId;
      referenceType = 'order';
    }

    // Record coupon usage if a coupon was applied
    if (coupon && coupon.id) {
      try {
        await API.post(`/coupons/${coupon.id}/use`, {
          buyerId: user?.id || null,
          orderId: orderId || null,
          discountAmount: couponDiscount
        });
      } catch (err) {
        console.error('Failed to record coupon usage:', err);
      }
    }

    return { referenceId, referenceType };
  };

  // ── Handle Stripe payment ─────────────────────────────────────────
  const handleStripeToken = async (paymentMethodId) => {
    setProcessing(true); setError('');
    try {
      const { referenceId, referenceType } = await createOrderOrBooking();

      // Create PaymentIntent (no auth required)
      const { data: intentData } = await API.post('/payments/stripe/intent', {
        referenceId, referenceType, amount: total,
      });

      // Confirm card — with automatic capture status becomes 'succeeded' directly
      const stripe = await stripePromise;
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        intentData.clientSecret,
        { payment_method: paymentMethodId }
      );

      if (stripeError) throw new Error(stripeError.message);
      if (!['succeeded', 'requires_capture'].includes(paymentIntent.status)) {
        throw new Error(`Payment not completed (status: ${paymentIntent.status})`);
      }

      // Notify backend
      await API.post('/payments/stripe/confirm', {
        intentId: paymentIntent.id, referenceId, referenceType,
      });

      clearCart();
      setStep(STEP_CONFIRM);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Payment failed. Please try again.');
    } finally { setProcessing(false); }
  };

  // ── Handle Coinbase payment ───────────────────────────────────────
  const handleCoinbase = async () => {
    setProcessing(true); setError('');
    try {
      const { referenceId, referenceType } = await createOrderOrBooking();
      const { data } = await API.post('/payments/coinbase/charge', {
        referenceId, referenceType: referenceType,
        amount: total, itemTitle: listing.title, buyerId: user?.id,
      });
      setCbCharge(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not create crypto charge');
    } finally { setProcessing(false); }
  };

  // ── Step progress bar ─────────────────────────────────────────────
  const StepBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 36, maxWidth: 520, margin: '0 auto 36px' }}>
      {steps.map((label, i) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${i <= step ? 'var(--cherry)' : 'var(--border)'}`, background: i < step ? 'var(--cherry)' : i === step ? 'var(--cherry)' : 'white', color: i <= step ? 'white' : 'var(--text-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, zIndex: 1 }}>
              {i < step ? '✓' : i + 1}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: i === step ? 'var(--cherry)' : 'var(--text-soft)', whiteSpace: 'nowrap' }}>{label}</div>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 2, background: i < step ? 'var(--cherry)' : 'var(--border)', margin: '0 4px', marginBottom: 18 }} />
          )}
        </div>
      ))}
    </div>
  );

  // ── Order summary sidebar ─────────────────────────────────────────
  const OrderSummary = () => (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 24, position: 'sticky', top: 88 }}>
      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Order Summary</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 56, height: 56, borderRadius: 10, background: 'var(--cream)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
          {listing.primary_image
            ? <img src={`${API_BASE}${listing.primary_image}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : '📦'}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{listing.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            {isRent ? `${rentDays} day${rentDays !== 1 ? 's' : ''}` : isAuction ? (cartItem?.buyItNow ? 'Buy It Now' : 'Winning bid') : 'Fixed price'}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-mid)' }}>{isRent ? `$${parseFloat(listing.price).toFixed(2)} × ${rentDays} days` : 'Item price'}</span>
          <span style={{ fontWeight: 600 }}>${itemPrice.toFixed(2)}</span>
        </div>
        {shipping > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-mid)' }}>Shipping</span>
            <span style={{ fontWeight: 600 }}>${shipping.toFixed(2)}</span>
          </div>
        )}
        {deposit > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-mid)' }}>Deposit (refundable)</span>
            <span style={{ fontWeight: 600 }}>${deposit.toFixed(2)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-mid)' }}>Platform fee (10%)</span>
          <span style={{ fontWeight: 600 }}>${platformFee.toFixed(2)}</span>
        </div>
        {couponDiscount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#4CAF50' }}>Coupon discount</span>
            <span style={{ fontWeight: 600, color: '#4CAF50' }}>-${couponDiscount.toFixed(2)}</span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 700 }}>Total</span>
        <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700 }}>${total.toFixed(2)}</span>
      </div>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {['🔒 Escrow protected', '💳 Card or 🪙 crypto', '🛡️ Buyer guarantee'].map(t => (
          <div key={t} style={{ fontSize: 11, color: 'var(--text-soft)' }}>{t}</div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 48px 80px' }}>
      <StepBar />

      {error && <div className="alert alert-error" style={{ maxWidth: 560, margin: '0 auto 20px' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 32, alignItems: 'start' }}>
        <div>

          {/* ── DECISION STEP (guests only) ── */}
          {checkoutMode === null && (
            <div style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div style={{ fontSize: 64, marginBottom: 20 }}>🛍️</div>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Checkout</h1>
              <p style={{ fontSize: 15, color: 'var(--text-mid)', marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
                Choose how you'd like to proceed with your purchase
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 380, margin: '0 auto' }}>
                <button onClick={() => setCheckoutMode('guest')}
                  style={{ padding: '20px 24px', background: 'white', border: '2px solid var(--border)', borderRadius: 14, cursor: 'pointer', textAlign: 'left', transition: 'all .2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--cherry)'; e.currentTarget.style.background = 'var(--cherry-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>👤 Guest Checkout</div>
                  <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>No account needed. Quick and easy.</div>
                </button>

                <button onClick={() => navigate('/login?redirect=/checkout')}
                  style={{ padding: '20px 24px', background: 'white', border: '2px solid var(--border)', borderRadius: 14, cursor: 'pointer', textAlign: 'left', transition: 'all .2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#635BFF'; e.currentTarget.style.background = '#F4F3FF'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#635BFF' }}>✅ Sign In</div>
                  <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>Track orders & earn rewards.</div>
                </button>
              </div>

              {user && (
                <div style={{ marginTop: 32, fontSize: 13, color: 'var(--text-soft)' }}>
                  Already signed in as <strong>{user.firstName}</strong>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: Cart review ── */}
          {checkoutMode && step === STEP_CART && (
            <div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Review Your Order</h2>

              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: isRent ? 20 : 0 }}>
                  <div style={{ width: 80, height: 80, borderRadius: 12, background: 'var(--cream)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
                    {listing.primary_image
                      ? <img src={`${API_BASE}${listing.primary_image}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : '📦'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>{listing.category_label}</div>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{listing.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>📍 {listing.location_city}, {listing.location_state}</div>
                  </div>
                </div>

                {isRent && (
                  <div style={{ paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Start Date</label>
                        <input type="date" className="form-input"
                          value={checkoutData.startDate || ''}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={e => setCheckout({ startDate: e.target.value })} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">End Date</label>
                        <input type="date" className="form-input"
                          value={checkoutData.endDate || ''}
                          min={checkoutData.startDate || new Date().toISOString().split('T')[0]}
                          onChange={e => {
                            const days = Math.max(1, Math.ceil((new Date(e.target.value) - new Date(checkoutData.startDate)) / 86400000));
                            setCheckout({ endDate: e.target.value, rentDays: days });
                          }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Coupon field */}
              <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <label className="form-label" style={{ marginBottom: 8 }}>Have a coupon code?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="form-input"
                    placeholder="Enter code (e.g., SAVE10)"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && validateCoupon()}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn btn-ghost"
                    onClick={validateCoupon}
                    disabled={!couponCode || couponLoading}
                  >
                    {couponLoading ? '⏳' : '✓ Apply'}
                  </button>
                </div>
                {coupon && (
                  <div style={{ fontSize: 12, color: '#4CAF50', marginTop: 8, fontWeight: 600 }}>
                    ✓ {coupon.discountType === 'percentage' ? `${coupon.discountAmount}% off` : `$${coupon.discountAmount} off`} applied!
                  </div>
                )}
                {couponError && (
                  <div style={{ fontSize: 12, color: '#F44336', marginTop: 8 }}>
                    {couponError}
                  </div>
                )}
              </div>

              {/* Guest vs sign-in prompt */}
              {!user && (
                <div style={{ background: '#F5F0EB', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>Already have an account?</div>
                    <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>Sign in to track your order and earn rewards.</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/login?redirect=/checkout`)}>Sign in</button>
                </div>
              )}

              {sellerBundle && user && (
                <div style={{ background: '#F3E5F5', border: '1px solid #CE93D8', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, color: '#6A1B9A' }}>🎁 Bundle Discount Available</div>
                  <div style={{ fontSize: 12, color: '#6A1B9A' }}>
                    Buy {sellerBundle.min_items}+ items from this seller and save {sellerBundle.discount_percent}% automatically.{' '}
                    <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate(`/seller/${listing.seller_id}`)}>Browse more listings →</span>
                  </div>
                </div>
              )}

              {listing.id_required && !user?.idVerified && (
                <div style={{ background:'#FFF8E1', border:'1.5px solid #FFD54F', borderRadius:12, padding:'16px 20px', marginBottom:16 }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>🪪 ID Verification Required</div>
                  <div style={{ fontSize:13, color:'#664D03', marginBottom:12 }}>The seller requires buyers to verify their identity before purchasing this item.</div>
                  <button className="btn btn-sm" style={{ background:'#856404', color:'white', border:'none' }} onClick={() => navigate('/dashboard/security')}>
                    Verify My ID →
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn btn-ghost" onClick={() => navigate('/browse')}>← Continue Shopping</button>
                <button className="btn btn-cherry" style={{ flex: 1 }}
                  disabled={!!(listing.id_required && !user?.idVerified)}
                  onClick={() => setStep(isGuest ? STEP_GUEST : STEP_SHIPPING)}>
                  Checkout →
                </button>
              </div>
            </div>
          )}

          {/* ── GUEST INFO STEP (guests only) ── */}
          {isGuest && step === STEP_GUEST && (
            <div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 6 }}>👤 Guest Checkout</h2>
              <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 24 }}>
                No account needed. Enter your details to receive your order confirmation.
              </p>

              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 20 }}>
                <div className="form-group">
                  <label className="form-label">Full Name <span className="required">*</span></label>
                  <input className="form-input" placeholder="Jane Smith" value={guest.name}
                    onChange={e => setGuest(p => ({ ...p, name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address <span className="required">*</span></label>
                  <input className="form-input" type="email" placeholder="jane@example.com" value={guest.email}
                    onChange={e => setGuest(p => ({ ...p, email: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Email <span className="required">*</span></label>
                  <input className="form-input" type="email" placeholder="jane@example.com" value={guest.confirmEmail}
                    onChange={e => setGuest(p => ({ ...p, confirmEmail: e.target.value }))} required />
                </div>
                {guest.confirmEmail && guest.email !== guest.confirmEmail && (
                  <div className="form-error">Email addresses do not match</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn btn-ghost" onClick={() => setStep(STEP_CART)}>← Back</button>
                <button className="btn btn-cherry" style={{ flex: 1 }}
                  disabled={!guest.name || !guest.email || guest.email !== guest.confirmEmail}
                  onClick={() => {
                    setAddress(p => ({ ...p, name: guest.name }));
                    setStep(STEP_SHIPPING);
                  }}>
                  Continue to Shipping →
                </button>
              </div>

              <div style={{ marginTop: 16, padding: '12px 16px', background: '#F5F0EB', borderRadius: 10, fontSize: 12, color: 'var(--text-soft)' }}>
                💡 Want to track your order, save favourites and earn rewards?{' '}
                <span style={{ color: 'var(--cherry)', cursor: 'pointer', fontWeight: 700 }}
                  onClick={() => navigate('/signup')}>Create a free account</span>
              </div>
            </div>
          )}

          {/* ── SHIPPING STEP ── */}
          {step === STEP_SHIPPING && (
            <div>
              {/* Show delivery method choice if both options available */}
              {(listing.local_pickup_only && listing.shipping_available) && !isRent && (
                <div style={{ marginBottom: 24 }}>
                  <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 16 }}>
                    📦 How do you want to receive this?
                  </h2>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div
                      onClick={() => setDeliveryMethod('pickup')}
                      style={{
                        padding: 16,
                        border: `2px solid ${deliveryMethod === 'pickup' ? 'var(--cherry)' : 'var(--border)'}`,
                        borderRadius: 12,
                        cursor: 'pointer',
                        background: deliveryMethod === 'pickup' ? 'var(--cherry-light)' : 'white',
                        transition: 'all 0.3s'
                      }}
                    >
                      <div style={{ fontSize: 20, marginBottom: 8 }}>📍</div>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Local Pick Up</div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Meet the seller in person</div>
                    </div>
                    <div
                      onClick={() => setDeliveryMethod('shipping')}
                      style={{
                        padding: 16,
                        border: `2px solid ${deliveryMethod === 'shipping' ? 'var(--cherry)' : 'var(--border)'}`,
                        borderRadius: 12,
                        cursor: 'pointer',
                        background: deliveryMethod === 'shipping' ? 'var(--cherry-light)' : 'white',
                        transition: 'all 0.3s'
                      }}
                    >
                      <div style={{ fontSize: 20, marginBottom: 8 }}>📦</div>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Ship to Me</div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Delivered to your address</div>
                    </div>
                  </div>
                </div>
              )}

              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 20 }}>
                {isRent ? '📍 Meetup Location' : (listing.local_pickup_only && !listing.shipping_available) ? '📍 Pickup Location' : (deliveryMethod === 'pickup' || (listing.local_pickup_only && !listing.shipping_available)) ? '📍 Pickup Location' : '📦 Delivery Address'}
              </h2>

              {(isRent || (listing.local_pickup_only && deliveryMethod !== 'shipping')) ? (
                <div>
                  <div className="alert alert-info" style={{ marginBottom: 20 }}>
                    📍 Choose a safe public meetup location for {isRent ? 'pickup and return' : 'pickup'}. We suggest nearby cafes, parks, and shopping centers.
                  </div>

                  {preferredLocations.length > 0 && (
                    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>✅ Seller's Preferred Meetup Locations</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 16 }}>
                        {preferredLocations.map((loc, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              setSelectedMeetup({ name: loc.name, address: loc.address, lat: loc.lat, lng: loc.lng });
                              setMeetupPlaces([]);
                              setCustomMeetup(null);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '12px 14px',
                              borderRadius: 10,
                              border: `1.5px solid ${selectedMeetup?.name === loc.name ? 'var(--cherry)' : 'var(--border)'}`,
                              background: selectedMeetup?.name === loc.name ? 'var(--cherry-light)' : 'white',
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              type="radio"
                              readOnly
                              checked={selectedMeetup?.name === loc.name}
                              style={{ accentColor: 'var(--cherry)' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>#{idx + 1} {loc.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{loc.address}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="alert alert-info" style={{ fontSize: 12 }}>
                        💡 Or select one of these locations, or enter a custom location below
                      </div>
                    </div>
                  )}

                  {!preferredLocations.length && (
                    <button
                      className="btn btn-cherry"
                      onClick={fetchPreferredLocations}
                      disabled={loadingPreferred}
                      style={{ marginBottom: 20 }}
                    >
                      {loadingPreferred ? '⏳ Loading...' : '💬 View Seller\'s Preferred Locations'}
                    </button>
                  )}

                  <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Or Select a Custom Meetup Location</div>
                    {error && <div className="alert alert-warning" style={{ marginBottom: 16 }}>⚠️ {error}</div>}

                    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                      <button className="btn btn-cherry" onClick={fetchNearbyPlaces} disabled={loadingPlaces}>
                        {loadingPlaces ? <><span className="spinner" /> Finding locations...</> : '📍 Find Nearby Safe Places'}
                      </button>
                      <button className="btn btn-cherry" onClick={() => setShowLocationModal(true)} style={{ background: '#27ae60' }}>
                        🗺️ Use Interactive Map
                      </button>
                      <button className="btn btn-ghost" onClick={() => setCustomMeetup({ name: '', address: '', label: 'Custom location' })}>
                        ✏️ Enter Custom Location
                      </button>
                    </div>

                    {!meetupPlaces.length && !selectedMeetup && !customMeetup && (
                      <div style={{ background: '#FFF3E0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>💡 Popular Safe Meetup Types</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {['☕ Coffee Shop', '🏪 Shopping Mall', '🌳 Park', '📚 Library', '🍽️ Restaurant', '📍 Community Center'].map(type => (
                            <button key={type}
                              onClick={() => setCustomMeetup({ name: type.split(' ').slice(1).join(' '), address: '', label: type })}
                              style={{ padding: '8px 12px', background: 'white', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {meetupPlaces.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: 'var(--text-soft)' }}>
                          Suggested Safe Meetup Locations
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                          {meetupPlaces.map(place => (
                            <div key={place.id} onClick={() => setSelectedMeetup(place)}
                              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${selectedMeetup?.id === place.id ? 'var(--cherry)' : 'var(--border)'}`, background: selectedMeetup?.id === place.id ? 'var(--cherry-light)' : 'white', cursor: 'pointer' }}>
                              <input type="radio" readOnly checked={selectedMeetup?.id === place.id} style={{ accentColor: 'var(--cherry)' }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>{place.name}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{place.address}</div>
                              </div>
                              {place.rating && <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>⭐ {place.rating}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {customMeetup && (
                      <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 16 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>📝 Enter Custom Meetup Location</div>
                        <div className="form-group">
                          <label className="form-label">Location Name <span className="required">*</span></label>
                          <input className="form-input" placeholder="e.g., Starbucks on Main St" value={customMeetup.name || ''} onChange={e => setCustomMeetup(p => ({ ...p, name: e.target.value }))} required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Address <span className="required">*</span></label>
                          <input className="form-input" placeholder="e.g., 123 Main St, Portland, OR 97201" value={customMeetup.address || ''} onChange={e => setCustomMeetup(p => ({ ...p, address: e.target.value }))} required />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-cherry" style={{ flex: 1 }}
                            disabled={!customMeetup.name?.trim() || !customMeetup.address?.trim()}
                            onClick={() => {
                              if (customMeetup.name?.trim() && customMeetup.address?.trim()) {
                                setSelectedMeetup(customMeetup);
                                setMeetupPlaces([]);
                              }
                            }}>
                            ✅ Use This Location
                          </button>
                          <button className="btn btn-ghost" onClick={() => setCustomMeetup(null)}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {selectedMeetup && !customMeetup && (
                      <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 16, marginTop: 16, border: '2px solid var(--green)', cursor: 'pointer', transition: 'all .2s' }}
                        onClick={() => {
                          if (selectedMeetup.lat && selectedMeetup.lng) {
                            window.open(`https://maps.google.com/?q=${selectedMeetup.lat},${selectedMeetup.lng}`, '_blank');
                          }
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 20 }}>✅</span>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>Meetup Location Selected</div>
                          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-soft)' }}>Click to view on map</span>
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>📍 {selectedMeetup.name}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>{selectedMeetup.address}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
                  <div className="form-group">
                    <label className="form-label">Full Name <span className="required">*</span></label>
                    <input className="form-input" value={address.name} onChange={e => setAddress(p => ({ ...p, name: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Street Address <span className="required">*</span></label>
                    <input className="form-input" placeholder="123 Main St" value={address.street} onChange={e => setAddress(p => ({ ...p, street: e.target.value }))} required />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">City <span className="required">*</span></label>
                      <input className="form-input" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State</label>
                      <input className="form-input" placeholder="PA" value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ZIP</label>
                      <input className="form-input" placeholder="18336" value={address.zip} onChange={e => setAddress(p => ({ ...p, zip: e.target.value }))} />
                    </div>
                  </div>

                  {listing.shipping_available && (
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>Shipping Method</div>
                        <button className="btn btn-ghost btn-sm" onClick={fetchRates} disabled={loadingRates || !address.zip}>
                          {loadingRates ? <><span className="spinner spinner-dark" /> Getting rates...</> : '🔄 Get Rates'}
                        </button>
                      </div>

                      {listing.free_shipping && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--green)', background: 'var(--green-light)', marginBottom: 10 }}>
                          <input type="radio" checked readOnly style={{ accentColor: 'var(--cherry)' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>Free Shipping</div>
                            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Paid by seller · 3-7 business days</div>
                          </div>
                          <span style={{ fontWeight: 700, color: 'var(--green)' }}>FREE</span>
                        </div>
                      )}

                      {rates.map(r => (
                        <div key={r.id} onClick={() => setSelectedRate(r)}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${selectedRate?.id === r.id ? 'var(--cherry)' : 'var(--border)'}`, background: selectedRate?.id === r.id ? 'var(--cherry-light)' : 'white', cursor: 'pointer', marginBottom: 8 }}>
                          <input type="radio" readOnly checked={selectedRate?.id === r.id} style={{ accentColor: 'var(--cherry)' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{r.carrier} {r.service}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{r.days ? `${r.days} business day${r.days !== 1 ? 's' : ''}` : 'Estimated delivery'}</div>
                          </div>
                          <span style={{ fontWeight: 700 }}>${parseFloat(r.rate).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Pickup Date Selection for Local Pickup */}
              {(isRent || (listing.local_pickup_only && deliveryMethod !== 'shipping')) && selectedMeetup && (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginTop: 20, marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>📅 {isRent ? 'Rental Start Date' : 'Preferred Pickup Date'}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8 }}>
                    {Array.from({ length: 7 }).map((_, i) => {
                      const date = new Date();
                      date.setDate(date.getDate() + i);
                      const dateStr = date.toISOString().split('T')[0];
                      const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
                      const isSelected = pickupDate === dateStr;

                      return (
                        <button
                          key={dateStr}
                          onClick={() => setPickupDate(dateStr)}
                          style={{
                            padding: '12px 8px',
                            border: `2px solid ${isSelected ? 'var(--cherry)' : 'var(--border)'}`,
                            borderRadius: 8,
                            background: isSelected ? 'var(--cherry-light)' : 'white',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            transition: 'all 0.2s',
                            textAlign: 'center'
                          }}
                        >
                          <div>{dayName}</div>
                          <div>{date.getMonth() + 1}/{date.getDate()}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pickup Time Selection */}
              {(isRent || (listing.local_pickup_only && deliveryMethod !== 'shipping')) && selectedMeetup && pickupDate && (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>⏰ Pickup Time Window</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                    {['08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-13:00', '14:00-15:00', '15:00-16:00', '16:00-17:00'].map(slot => (
                      <button
                        key={slot}
                        onClick={() => setPickupTime(slot)}
                        style={{
                          padding: '12px 8px',
                          border: `2px solid ${pickupTime === slot ? 'var(--cherry)' : 'var(--border)'}`,
                          borderRadius: 8,
                          background: pickupTime === slot ? 'var(--cherry-light)' : 'white',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 12 }}>
                    💡 Seller will confirm availability within this time window
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setStep(isGuest ? STEP_GUEST : STEP_CART)}>← Back</button>
                <button
                  className="btn btn-cherry"
                  style={{ flex: 1 }}
                  onClick={() => setStep(STEP_PAYMENT)}
                  disabled={(isRent || listing.local_pickup_only) && (!selectedMeetup || !pickupDate)}
                  title={(isRent || listing.local_pickup_only) && !selectedMeetup ? 'Please select a meetup location' : (isRent || listing.local_pickup_only) && !pickupDate ? 'Please select a pickup date' : ''}
                >
                  Continue to Payment →
                </button>
              </div>
            </div>
          )}

          {/* ── PAYMENT STEP ── */}
          {step === STEP_PAYMENT && (
            <div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 20 }}>💳 Payment</h2>

              {/* Guest summary banner */}
              {isGuest && (
                <div style={{ background: '#F5F0EB', borderRadius: 12, padding: '12px 16px', marginBottom: 18, fontSize: 13, color: 'var(--text-mid)' }}>
                  Confirmation will be sent to <strong>{guest.email}</strong>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                {[
                  { id: 'stripe',   label: 'Card',   icon: '💳', color: '#635BFF', bg: '#F4F3FF' },
                  { id: 'coinbase', label: 'Crypto', icon: '🪙', color: '#0052FF', bg: '#EBF0FF' },
                ].map(m => (
                  <div key={m.id} onClick={() => setPayMethod(m.id)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 12, border: `2px solid ${payMethod === m.id ? m.color : 'var(--border)'}`, background: payMethod === m.id ? m.bg : 'white', cursor: 'pointer' }}>
                    <span style={{ fontSize: 22 }}>{m.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: payMethod === m.id ? m.color : 'var(--text-primary)' }}>{m.label}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', width: 18, height: 18, borderRadius: '50%', border: `2px solid ${payMethod === m.id ? m.color : 'var(--border)'}`, background: payMethod === m.id ? m.color : 'white' }} />
                  </div>
                ))}
              </div>

              {payMethod === 'stripe' && (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
                  <div style={{ background: '#F4F3FF', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#635BFF', fontWeight: 600 }}>
                    🔒 Secured by Stripe — PCI DSS Level 1
                  </div>
                  <Elements stripe={stripePromise}>
                    <StripeCardForm onToken={handleStripeToken} processing={processing} />
                  </Elements>
                </div>
              )}

              {payMethod === 'coinbase' && (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
                  <div style={{ background: '#EBF0FF', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#0052FF', fontWeight: 600 }}>
                    🪙 Powered by Coinbase Commerce
                  </div>
                  {cbCharge ? (
                    <div>
                      <div className="alert alert-success" style={{ marginBottom: 16 }}>✅ Charge created! Open Coinbase to complete payment.</div>
                      <a href={cbCharge.hostedUrl} target="_blank" rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#0052FF', color: 'white', padding: '14px', borderRadius: 11, fontSize: 15, fontWeight: 700, textDecoration: 'none', marginBottom: 10 }}>
                        🪙 Open Coinbase Checkout →
                      </a>
                      <button className="btn btn-green btn-full" onClick={() => { clearCart(); setStep(STEP_CONFIRM); }}>✅ I've sent the payment</button>
                    </div>
                  ) : (
                    <div>
                      <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 20, lineHeight: 1.65 }}>
                        Pay with Bitcoin, Ethereum, USDC, Litecoin or DAI. Your payment will be held in escrow.
                      </p>
                      <button className="btn btn-full" onClick={handleCoinbase} disabled={processing}
                        style={{ background: processing ? '#888' : '#0052FF', color: 'white', border: 'none', borderRadius: 11, padding: '14px', fontSize: 15, fontWeight: 700 }}>
                        {processing ? <><span className="spinner" /> Creating charge...</> : `🪙 Pay $${total.toFixed(2)} in Crypto`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={() => setStep(STEP_SHIPPING)}>← Back</button>
            </div>
          )}

          {/* ── CONFIRMATION STEP ── */}
          {step === STEP_CONFIRM && (
            <div style={{ padding: '40px 0', background: 'var(--cream)' }}>
              <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                {/* Success banner */}
                <div style={{ textAlign: 'center', marginBottom: 40, paddingBottom: 32 }}>
                  <div style={{ fontSize: 80, marginBottom: 16, animation: 'pulse 0.6s ease' }}>✅</div>
                  <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 700, marginBottom: 12, color: 'var(--cherry)' }}>
                    {isRent ? 'Booking Confirmed!' : 'Order Confirmed!'}
                  </h1>
                  <p style={{ fontSize: 16, color: 'var(--text-mid)' }}>
                    Thank you! Your {isRent ? 'booking' : 'order'} has been received and is being processed.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 32 }}>
                  {/* Order/Booking number card */}
                  <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '2px solid var(--cherry)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>
                      {isRent ? 'Booking ID' : 'Order Number'}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: 'var(--cherry)', marginBottom: 8, wordBreak: 'break-all' }}>
                      {orderNumber || bookingId?.substring(0, 12).toUpperCase() || (isRent ? 'BK-XXXX' : 'ORD-XXXX')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                      {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>

                  {/* Confirmation email card */}
                  <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>📧 Confirmation Email</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                      {isGuest ? guest.email : user?.email}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Check your email for receipt & tracking</div>
                  </div>

                  {/* Estimated delivery card */}
                  <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>
                      {isRent ? '📅 Pickup' : '🚚 Estimated Delivery'}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                      {isRent || (deliveryMethod === 'pickup') ? (
                        <>
                          {pickupDate && new Date(pickupDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {pickupTime && <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{pickupTime}</div>}
                        </>
                      ) : selectedRate?.days ? (
                        `${selectedRate.days}-${selectedRate.days + 2} business days`
                      ) : (
                        'Varies by location'
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                      {isRent || deliveryMethod === 'pickup' ? 'Seller will confirm availability' : 'Standard shipping'}
                    </div>
                  </div>
                </div>

                {/* Item & Pricing Details */}
                <div style={{ background: 'white', borderRadius: 14, padding: 24, marginBottom: 20, border: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>📦 Order Summary</h3>

                  {/* Item card */}
                  <div style={{ display: 'flex', gap: 16, paddingBottom: 20, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
                    {listing.primary_image && (
                      <img src={`${API_BASE}${listing.primary_image}`} alt={listing.title}
                        style={{ width: 100, height: 100, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{listing.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 8 }}>
                        Seller: <strong>{listing.first_name} {listing.last_name}</strong>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                        {isRent ? `${rentDays}-day rental` : listing.listing_type === 'auction' ? 'Auction purchase' : 'Purchase'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                      ${itemPrice.toFixed(2)}
                    </div>
                  </div>

                  {/* Price breakdown */}
                  <div style={{ marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13 }}>
                      <span style={{ color: 'var(--text-soft)' }}>Item Price</span>
                      <span>${itemPrice.toFixed(2)}</span>
                    </div>
                    {shipping > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-soft)' }}>Shipping ({selectedRate?.carrier})</span>
                        <span>${shipping.toFixed(2)}</span>
                      </div>
                    )}
                    {deposit > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-soft)' }}>Security Deposit</span>
                        <span>${deposit.toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 13 }}>
                      <span style={{ color: 'var(--text-soft)' }}>Platform Fee (10%)</span>
                      <span>${platformFee.toFixed(2)}</span>
                    </div>
                    {couponDiscount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 13, color: 'var(--green)' }}>
                        <span>Coupon Discount</span>
                        <span>-${couponDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '2px solid var(--border)', fontWeight: 700, fontSize: 16 }}>
                      <span>Total Amount</span>
                      <span style={{ color: 'var(--cherry)' }}>${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Delivery/Pickup Details */}
                {(isRent || deliveryMethod === 'pickup') ? (
                  <div style={{ background: 'white', borderRadius: 14, padding: 24, marginBottom: 20, border: '1px solid var(--border)' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📍 {isRent ? 'Rental' : 'Pickup'} Details</h3>
                    <div style={{ background: 'var(--cream)', borderRadius: 10, padding: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{selectedMeetup?.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 12 }}>{selectedMeetup?.address}</div>
                      <div style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Date & Time</div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {pickupDate && new Date(pickupDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                          {pickupTime && ` · ${pickupTime}`}
                        </div>
                      </div>
                      <div style={{ marginTop: 12, padding: '12px 0', borderTop: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Contact Seller</div>
                        <button className="btn btn-ghost btn-sm" onClick={() => window.location.href = `/messages?seller=${listing.seller_id}`}>
                          💬 Send Message
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: 'white', borderRadius: 14, padding: 24, marginBottom: 20, border: '1px solid var(--border)' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📦 Shipping To</h3>
                    <div style={{ background: 'var(--cream)', borderRadius: 10, padding: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{address.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>
                        {address.street}<br />
                        {address.city}, {address.state} {address.zip}<br />
                        {address.country}
                      </div>
                      {selectedRate && (
                        <div style={{ marginTop: 12, padding: '12px 0', borderTop: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>Shipping Method</div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {selectedRate.carrier} {selectedRate.service} ({selectedRate.days} business days)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Next steps */}
                <div style={{ background: '#E8F5E9', borderRadius: 14, padding: 24, marginBottom: 32, border: '2px solid var(--green)' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--green)' }}>✨ What Happens Next?</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 24, flexShrink: 0 }}>1️⃣</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Payment Secured</div>
                        <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Held in escrow until {isRent ? 'pickup confirmed' : 'delivery'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 24, flexShrink: 0 }}>2️⃣</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Seller Notified</div>
                        <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Receives your {isRent ? 'booking' : 'order'} immediately</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 24, flexShrink: 0 }}>3️⃣</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Confirmation Email</div>
                        <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Receipt & tracking info sent</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', paddingBottom: 40 }}>
                  {user ? (
                    <>
                      <button className="btn btn-cherry btn-lg" onClick={() => navigate(isRent ? '/dashboard/bookings' : '/dashboard/orders')}>
                        {isRent ? '📅 View My Bookings' : '📦 View My Orders'}
                      </button>
                      <button className="btn btn-ghost" onClick={() => navigate('/browse')}>Continue Shopping</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-cherry btn-lg" onClick={() => navigate('/browse')}>Continue Shopping</button>
                      <button className="btn btn-ghost" onClick={() => navigate(`/login?redirect=/dashboard/orders`)}>Sign In to Track</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Order summary — shown on all steps except confirm */}
        {step !== STEP_CONFIRM && <OrderSummary />}
      </div>

      {/* Meetup Location Modal */}
      <MeetupLocationModal
        isOpen={showLocationModal}
        listing={listing}
        onSelect={handleLocationSelect}
        onCancel={() => setShowLocationModal(false)}
      />
    </div>
  );
}
